import { useEffect, useMemo, useRef } from 'react';
import Cookies from 'js-cookie';
import { isInIframe } from '../utils/globalConfig';

// navigator.modelContext and window.PageTools are single browser globals shared
// with everything else on the page, so the bookkeeping that mirrors them is
// module scoped: it has to outlive any one React tree and stay reachable from
// the imperative registerPageTools/unregisterPageTools exports.
const ownerRegistrations = new Map();
let registrationGeneration = 0;

const getModelContext = () =>
  document.modelContext ?? navigator.modelContext;

const setModelContext = context => {
  Object.defineProperty(navigator, 'modelContext', {
    value: context,
    configurable: true,
    writable: true,
  });
};

const removeOwnedTools = (tools, ownedTools) => {
  const ownedSet = new Set(ownedTools);
  return (tools ?? []).filter(tool => !ownedSet.has(tool));
};

const mergeToolsByName = (...toolGroups) => {
  const mergedTools = new Map();
  toolGroups.forEach(tools => {
    tools.forEach(tool => {
      if (!mergedTools.has(tool.name)) {
        mergedTools.set(tool.name, tool);
      }
    });
  });
  return [...mergedTools.values()];
};

const syncPageTools = (context, additionalTools) => {
  const tools = mergeToolsByName(
    context.tools,
    window.PageTools?.tools ?? [],
    additionalTools
  );
  window.PageTools = {
    tools,
    isReady: tools.length > 0,
  };
};

const unregisterOwner = (ownerId, generation) => {
  const registration = ownerRegistrations.get(ownerId);
  if (
    !registration ||
    (generation !== undefined && registration.generation !== generation)
  ) {
    return;
  }

  registration.controllers.forEach(controller => controller.abort());
  registration.context.tools = removeOwnedTools(
    registration.context.tools,
    registration.tools
  );

  if (window.PageTools) {
    const remainingTools = removeOwnedTools(
      window.PageTools.tools,
      registration.tools
    );
    window.PageTools = {
      tools: remainingTools,
      isReady: remainingTools.length > 0,
    };
  }

  ownerRegistrations.delete(ownerId);
};

export const unregisterPageTools = ownerId => {
  unregisterOwner(ownerId);
};

const collectExistingToolNames = () => {
  const names = new Set();
  getModelContext()?.tools?.forEach(tool => names.add(tool.name));
  window.PageTools?.tools?.forEach(tool => names.add(tool.name));
  return names;
};

export const registerPageTools = (ownerId, tools) => {
  unregisterOwner(ownerId);

  if (tools.length === 0) {
    return () => undefined;
  }

  const existingNames = collectExistingToolNames();
  const acceptedTools = tools.filter(tool => {
    if (!existingNames.has(tool.name)) {
      existingNames.add(tool.name);
      return true;
    }

    console.warn(`Page tool "${tool.name}" is already registered.`);
    return false;
  });

  if (acceptedTools.length === 0) {
    return () => undefined;
  }

  let context = getModelContext();
  if (!context) {
    context = { tools: [] };
    setModelContext(context);
  }

  context.tools = mergeToolsByName(context.tools ?? [], acceptedTools);

  const controllers = [];
  if (typeof context.registerTool === 'function') {
    acceptedTools.forEach(tool => {
      const controller = new AbortController();
      try {
        context.registerTool(
          {
            ...tool,
            handler: tool.execute,
          },
          { signal: controller.signal }
        );
        controllers.push(controller);
      } catch (error) {
        controller.abort();
        console.error(`Unable to register page tool "${tool.name}".`, error);
      }
    });
    context.tools = mergeToolsByName(context.tools);
  }

  syncPageTools(context, acceptedTools);

  registrationGeneration += 1;
  const generation = registrationGeneration;
  ownerRegistrations.set(ownerId, {
    generation,
    context,
    tools: acceptedTools,
    controllers,
  });

  return () => unregisterOwner(ownerId, generation);
};

export const ITP_PAGE_TOOLS_COOKIE = 'isItpPagetools';

// Read per call rather than once at import: the cookie can be set or cleared
// after the bundle has loaded, and the gate has to follow it without a reload.
export const isItpPageToolsCookieEnabled = () =>
  String(Cookies.get(ITP_PAGE_TOOLS_COOKIE)).trim().toLowerCase() === 'true';

// Embedded instances stay silent so the host page keeps sole ownership of the
// model context its own agent reads.
export const isInternationalPageToolsEnabled = (
  enabled = isItpPageToolsCookieEnabled(),
  embedded = isInIframe
) => enabled === true && embedded !== true;

// Callers rebuild their tool array on every render, so registration is keyed to
// what an agent can actually observe rather than to object identity.
const getToolsSignature = tools =>
  JSON.stringify(
    tools.map(tool => [
      tool.name,
      tool.description,
      tool.inputSchema,
      tool.annotations,
    ])
  );

const staleToolResult = name => ({
  content: [
    { type: 'text', text: `Page tool "${name}" is no longer available.` },
  ],
  isError: true,
});

// Registered tools delegate to the newest handler for their name so a long-lived
// registration never runs a handler that captured stale page state.
const createDelegatingTools = (tools, toolsRef) =>
  tools.map(({ execute, ...definition }) => ({
    ...definition,
    execute: (...args) => {
      const current = toolsRef.current.find(
        tool => tool.name === definition.name
      );
      return current
        ? current.execute(...args)
        : staleToolResult(definition.name);
    },
  }));

export const usePageTools = (
  ownerId,
  tools,
  enabled = isInternationalPageToolsEnabled()
) => {
  const toolsRef = useRef(tools);
  const signature = getToolsSignature(tools);

  useEffect(() => {
    toolsRef.current = tools;
  }, [tools]);

  // Handlers reach the agent through the ref, so a new tools array only matters
  // here when its signature changed.
  const registeredTools = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => createDelegatingTools(tools, toolsRef),
    [signature]
  );

  useEffect(() => {
    if (!enabled) {
      unregisterOwner(ownerId);
      return undefined;
    }

    return registerPageTools(ownerId, registeredTools);
  }, [enabled, ownerId, registeredTools]);
};
